import { API_CONFIG } from '../../config/api';
import { ApiValidationError } from '../../types/auth';
import type {
  Quotation,
  CreateQuotationData,
  UpdateQuotationData,
  CopyQuotationData,
  ConvertQuotationToInvoiceData,
  SendQuotationData,
  QuotationsResponse,
  QuotationResponse,
  GetQuotationsParams,
  QuotationsResult,
} from '../../types/sales/quotation';
import type { Invoice, InvoiceResponse } from '../../types/invoice';
import { parseContentDispositionFilename } from '../contentDisposition';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string): Promise<never> => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

const buildQuotationsQuery = (params?: GetQuotationsParams): string => {
  if (!params) return '';

  const query = new URLSearchParams();

  if (params.search) {
    query.set('search', params.search);
  }
  if (params.customer_id != null) {
    query.set('customer_id', String(params.customer_id));
  }
  if (params.status) {
    query.set('status', params.status);
  }
  if (params.quote_date_from) {
    query.set('quote_date_from', params.quote_date_from);
  }
  if (params.quote_date_to) {
    query.set('quote_date_to', params.quote_date_to);
  }
  if (params.sort) {
    query.set('sort', params.sort);
  }
  if (params.per_page != null) {
    query.set('per_page', String(params.per_page));
  }
  if (params.page != null) {
    query.set('page', String(params.page));
  }

  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export const getQuotations = async (params?: GetQuotationsParams): Promise<QuotationsResult> => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.GET_ALL}${buildQuotationsQuery(params)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch quotations');
  }

  const result: QuotationsResponse = await response.json();
  return {
    quotations: result.data ?? [],
    meta: result.meta,
  };
};

export const getQuotationById = async (id: number): Promise<Quotation> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.GET_BY_ID}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch quotation');
  }

  const result: QuotationResponse = await response.json();
  return result.data;
};

export const createQuotation = async (data: CreateQuotationData): Promise<Quotation> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create quotation');
  }

  const result: QuotationResponse = await response.json();
  return result.data;
};

export const updateQuotation = async (
  id: number,
  data: UpdateQuotationData
): Promise<Quotation> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.UPDATE}/${id}`,
    {
      method: 'PUT',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update quotation');
  }

  const result: QuotationResponse = await response.json();
  return result.data;
};

export const deleteQuotation = async (id: number): Promise<void> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.DELETE}/${id}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete quotation');
  }
};

export const copyQuotation = async (
  id: number,
  data?: CopyQuotationData
): Promise<Quotation> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.COPY}/${id}/copy`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data ?? {}),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to copy quotation');
  }

  const result: QuotationResponse = await response.json();
  return result.data;
};

export const convertQuotationToInvoice = async (
  id: number,
  data?: ConvertQuotationToInvoiceData
): Promise<Invoice> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.CONVERT_TO_INVOICE}/${id}/convert-to-invoice`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data ?? {}),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to convert quotation to invoice');
  }

  const result: InvoiceResponse = await response.json();
  return result.data;
};

export const fetchQuotationPdf = async (
  id: number,
  fallbackFilename?: string
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.PDF}/${id}/pdf`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to load quotation PDF');
  }

  const blob = await response.blob();
  const filename =
    parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
    fallbackFilename ??
    `quotation-${id}.pdf`;

  return { blob, filename };
};

export const downloadQuotationPdf = async (
  id: number,
  fallbackFilename?: string
): Promise<void> => {
  const { blob, filename } = await fetchQuotationPdf(id, fallbackFilename);

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

export const sendQuotation = async (
  id: number,
  data?: SendQuotationData
): Promise<Quotation> => {
  const payload: SendQuotationData = {};
  if (data?.to?.trim()) payload.to = data.to.trim();
  if (data?.subject?.trim()) payload.subject = data.subject.trim();
  if (data?.message?.trim()) payload.message = data.message.trim();
  if (data?.cc != null) {
    if (Array.isArray(data.cc)) {
      const emails = data.cc.map((e) => e.trim()).filter(Boolean);
      if (emails.length === 1) payload.cc = emails[0];
      else if (emails.length > 1) payload.cc = emails;
    } else if (data.cc.trim()) {
      payload.cc = data.cc.trim();
    }
  }

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.QUOTATIONS.SEND}/${id}/send`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to send quotation');
  }

  const result: QuotationResponse = await response.json();
  return result.data;
};
